// ─── Auth API ───

import type {
  AuthCallbackResponse,
  AuthenticatedUser,
  DevPersonaRole,
  MeResponse,
} from '@a8-planner/shared';
import { apiClient, type RequestOptions } from '../api-client';

interface LoginUrlResponse {
  data: {
    url: string;
    state: string;
  };
}

/** Actual response shape from GET /auth/me (backend wraps in { data: ... }) */
interface MeApiResponse {
  data: MeResponse;
}

/** Actual response shape from POST /auth/callback */
interface CallbackApiResponse {
  data: AuthCallbackResponse;
}

/** Actual response shape from POST /auth/dev-login */
interface DevLoginApiResponse {
  data: {
    user: AuthenticatedUser;
    // Issue #90 (freshness fix) — computed atomically at sign-in, same as
    // AuthCallbackResponse.isVacationReviewer.
    isVacationReviewer: boolean;
    // GHE #883 follow-up — same reason: the dev panel bypasses GET /auth/me, so without this the
    // global "+" would be missing for a whole session after a dev sign-in.
    canCreateTaskAnywhere: boolean;
  };
}

export const authApi = {
  /** Get the Entra ID login URL and CSRF state. */
  getLoginUrl(): Promise<LoginUrlResponse> {
    return apiClient.get<LoginUrlResponse>('/auth/login');
  },

  /** Exchange authorization code for token and user. */
  callback(code: string, state: string, expectedState: string): Promise<CallbackApiResponse> {
    return apiClient.post<CallbackApiResponse>('/auth/callback', {
      code,
      state,
      expectedState,
    });
  },

  /**
   * Get the currently authenticated user. Called on every cold load by
   * `AuthProvider.checkAuth` to probe the HttpOnly session cookie — pass
   * `{ suppressAuthRedirect: true }` from that call site so a 401 here
   * (genuinely logged out) doesn't trigger api-client's global redirect
   * before the app has finished mounting.
   */
  getMe(options?: RequestOptions): Promise<MeApiResponse> {
    return apiClient.get<MeApiResponse>('/auth/me', options);
  },

  /**
   * Sign out — clears the server-side HttpOnly cookie. Since Phase 10 the
   * session lives ENTIRELY in that cookie (the localStorage/Bearer fallback was
   * retired), so clearing it server-side is sufficient to end the session.
   */
  logout(): Promise<{ data: { message: string } }> {
    return apiClient.post<{ data: { message: string } }>('/auth/logout', {});
  },

  /**
   * DEV-ONLY — sets the HttpOnly cookie for the given role without Entra ID.
   * The backend returns 404 when dev-login is disabled (production).
   * Only call this from within an `import.meta.env.DEV` guard.
   */
  devLogin(role: DevPersonaRole): Promise<DevLoginApiResponse> {
    return apiClient.post<DevLoginApiResponse>('/auth/dev-login', { role });
  },
};
